import { BUKIETOWA_LOCATIONS } from '@/lib/globalFloristSchema';

/**
 * Treść strony /dostawa — wydzielona z pages/dostawa.tsx jako osobny,
 * JSON-serializable config. Akapity z osadzonym znacznikiem <strong>/<a>
 * (np. dymki informacyjne o dopłacie niedzielnej, kontakt telefoniczny)
 * zostają jako JSX bezpośrednio w dostawa.tsx — config nie zawiera JSX,
 * ten sam wzorzec co przy FuneralCollectionPage/dlaFirmContent.
 */
export interface BulletItem {
  iconKey: string;
  text: string;
}

export interface FaqEntry {
  q: string;
  a: string;
  group: 'delivery' | 'payment';
}

export interface DeliveryStepCard {
  num: string;
  title: string;
  desc: string;
}

export interface StorePickup {
  name: string;
  address: string;
  phone: string;
  href: string;
}

export interface PaymentMethodContent {
  iconKey: string;
  title: string;
  desc: string;
}

export interface PaymentProcessStep {
  n: string;
  title: string;
  desc: string;
}

export const DOSTAWA_CONTENT = {
  hero: {
    tagline: 'Kwiaty dla bliskiej osoby, nawet gdy nie możesz wręczyć ich osobiście.',
    headingLine1: 'Dostawa kwiatów',
    headingAccent: 'w Lublinie i okolicach',
    body: 'Wybierz kompozycję, podaj adres odbiorcy i sprawdź dostępny termin. Kwiaty możesz zamówić z dostawą albo odebrać w jednej z naszych kwiaciarni w Lublinie.',
    ctaInfoLabel: 'Sprawdź zasady dostawy',
    ctaPaymentLabel: 'Metody płatności',
  },
  addressChecker: {
    eyebrow: 'Zaplanuj dostawę',
    heading: 'Sprawdź dostępne terminy',
    body: 'Wybierz datę i przedział godzinowy - zobaczysz od razu, czy obowiązuje dopłata. Ostateczna cena dostawy zależy od adresu i zostanie wyliczona przy składaniu zamówienia.',
    bullets: [
      { iconKey: 'clock', text: 'Wybór dnia i przedziału dostawy' },
      { iconKey: 'truck', text: 'Dostawa w Lublinie i okolicach' },
      { iconKey: 'mapPin', text: 'Cena zależy od odległości od kwiaciarni' },
    ] satisfies BulletItem[],
    dateLabel: 'Data dostawy',
    sundayBadge: 'niedziela · od 40 zł',
    timeLabel: 'Godzina dostawy',
    sundaySuffix: '- niedziela',
    footerNote: 'Ostateczna cena dostawy zależy od odległości i zostanie obliczona automatycznie przy podaniu adresu w zamówieniu.',
  },
  sameDay: {
    eyebrow: 'Zaplanuj zamówienie',
    heading: 'Termin i koszt dostawy kwiatów',
    body: 'Termin wybierzesz podczas składania zamówienia. Koszt dostawy zależy od adresu, dnia oraz przedziału godzinowego i jest wyliczany po uzupełnieniu tych danych. Sprawdź go przed opłaceniem zamówienia.',
    availabilityHeading: 'Dostawa tego samego dnia - sprawdź dostępność',
    availabilityIntro: 'Zależy Ci na dostawie jeszcze dziś?',
    availabilityCta: 'Zapytaj nas o możliwość realizacji',
    availabilityNote: 'Zamówienia na dziś: poniedziałek–sobota do 18:00, niedziela do 15:00. Wybierz dostępny przedział podczas składania zamówienia.',
    importantEyebrow: 'Ważne',
    importantHeading: 'Preferowana godzina i przedział dostawy',
    importantBody: 'Wybierz jeden z dostępnych przedziałów dostawy. Wskazana godzina jest orientacyjna - dokładny czas doręczenia może zależeć od dostępności kwiatów, liczby zamówień oraz sytuacji na drodze. Nie traktuj jej jako gwarancji przekazania kwiatów o konkretnej minucie.',
    delayNote: 'Możliwe są niewielkie opóźnienia niezależne od nas, np. z powodu korków lub innych sytuacji losowych.',
  },
  absent: {
    eyebrow: 'Przekazanie kwiatów',
    heading: 'Kurier kwiatowy w Lublinie - jak wygląda doręczenie?',
    intro: 'Zamówiona kompozycja trafia pod wskazany adres. Podaj numer telefonu odbiorcy, aby kurier mógł ustalić przekazanie kwiatów. Jeśli nie zastanie go na miejscu, najpierw kontaktuje się z nim telefonicznie. Gdy nie ma odpowiedzi, kontaktujemy się z osobą zamawiającą.',
    optionsIntro: 'Jeśli odbiorca wyrazi zgodę, możemy:',
    options: [
      'Zostawić kwiaty u sąsiada lub sąsiadki',
      'Zostawić je pod drzwiami, jeśli odbiorca wkrótce wróci i warunki pozwalają na bezpieczne pozostawienie',
      'Ustalić odbiór zamówienia w jednej z naszych kwiaciarni',
    ] as string[],
    leaveWarning: 'Kwiatów nie należy zostawiać bez odpowiednich warunków na długi czas - mogą ucierpieć, szczególnie bez dostępu do wody.',
    noPickupHeading: 'Gdy nie możemy skontaktować się z odbiorcą',
    noPickupBody: 'Jeżeli nie uda się skontaktować ani z odbiorcą, ani z osobą zamawiającą, kurier wraca z zamówieniem do kwiaciarni.',
    recipientDataHeading: 'Dane odbiorcy i wskazówki dla kuriera',
    recipientDataItems: [
      'Imię i nazwisko odbiorcy',
      'Numer telefonu odbiorcy',
      'Dokładny adres - ulica i numer domu',
      'Numer mieszkania lub lokalu',
      'Piętro, jeśli ma znaczenie',
      'Kod do furtki lub domofonu (jeśli potrzebny)',
      'Dodatkowe wskazówki dla kuriera',
    ] as string[],
    recipientDataFooter: 'Sprawdź numer telefonu i adres przed zatwierdzeniem zamówienia. Numer lokalu lub kod do domofonu ułatwi kurierowi dotarcie do odbiorcy.',
  },
  pickup: {
    eyebrow: 'Odbiór osobisty',
    heading: 'Odbiór kwiatów w naszych kwiaciarniach',
    body: 'Chcesz wręczyć kwiaty samodzielnie? W zamówieniu wybierz odbiór osobisty, wskaż kwiaciarnię i dostępny termin. Poniżej znajdziesz adresy punktów odbioru.',
    freeBadge: 'Odbiór osobisty jest bezpłatny',
    hoursLabel: 'Godziny odbioru',
    hoursRows: [
      { label: 'Poniedziałek–sobota', value: '9:00–19:00' },
      { label: 'Niedziela', value: '10:00–16:00' },
    ] as { label: string; value: string }[],
    footerNote: 'Termin odbioru wybierzesz podczas składania zamówienia. Przy pilnym zamówieniu skontaktuj się z nami, aby potwierdzić możliwość przygotowania wybranej kompozycji.',
    stores: BUKIETOWA_LOCATIONS.map(location => ({
      name: location.pickupName,
      address: `${location.address.streetAddress}, ${location.address.addressLocality}`,
      phone: location.telephone,
      href: location.pickupHref,
    })) satisfies StorePickup[],
  },
  how: {
    eyebrow: 'Jak to działa',
    heading: 'Jak zamówić przesyłkę kwiatową w Lublinie?',
    intro: 'Zacznij od kompozycji dopasowanej do okazji. Przejrzyj',
    bouquetLinkLabel: 'bukiety',
    linkJoin: 'lub',
    flowerBoxLinkLabel: 'flower boxy',
    introEnd: ', a następnie wybierz wariant i uzupełnij szczegóły zamówienia.',
    steps: [
      { num: '01', title: 'Wybierz kompozycję', desc: 'Porównaj zdjęcia i opis produktu. Wybierz dostępny wariant oraz dodatki, które chcesz dołączyć.' },
      { num: '02', title: 'Dane odbiorcy i bilecik', desc: 'Wpisz adres i telefon odbiorcy. Dodaj wiadomość na bileciku oraz wskazówki, które pomogą kurierowi dotrzeć na miejsce.' },
      { num: '03', title: 'Termin i płatność', desc: 'Wybierz dostępny dzień i przedział dostawy. Sprawdź koszt zamówienia i opłać je online.' },
      { num: '04', title: 'Przekazanie kwiatów', desc: 'Przygotujemy wybraną kompozycję i dostarczymy ją pod podany adres. Zasady kontaktu i postępowania przy nieobecności odbiorcy znajdziesz powyżej.' },
    ] satisfies DeliveryStepCard[],
  },
  faq: {
    eyebrow: 'FAQ',
    heading: 'Pytania o dostawę i płatności',
    deliveryGroupLabel: 'Dostawa',
    paymentGroupLabel: 'Płatności',
    contactCtaLabel: 'Skontaktuj się z nami',
    helpCenterCtaLabel: 'Centrum pomocy',
    items: [
      { group: 'delivery', q: 'Czy dostarczacie kwiaty poza Lublin?', a: 'Dostarczamy kwiaty w Lublinie i okolicach. Wpisz dokładny adres w zamówieniu, aby sprawdzić dostępność i koszt dostawy. Jeśli nie możesz wybrać danego adresu lub dostawa nie zostanie potwierdzona, skontaktuj się z nami.' },
      { group: 'delivery', q: 'Ile kosztuje dostawa?', a: 'Cena zależy od adresu, dnia oraz wybranego przedziału godzinowego. Koszt zostanie wyliczony po uzupełnieniu danych dostawy w zamówieniu, przed płatnością.' },
      { group: 'delivery', q: 'Czy mogę zamówić dostawę tego samego dnia?', a: 'Zamówienia na dziś przyjmujemy od poniedziałku do soboty do 18:00, a w niedzielę do 15:00. Dostępność konkretnego przedziału dostawy lub odbioru sprawdzisz podczas składania zamówienia.' },
      { group: 'delivery', q: 'Od czego zależy dostępność godzin dostawy?', a: 'Dostępne przedziały zależą od wybranego dnia i godziny składania zamówienia. Wybierz jeden z dostępnych terminów. Jeśli żaden nie odpowiada Twoim potrzebom, zapytaj nas o możliwość realizacji.' },
      { group: 'delivery', q: 'Czy mogę wskazać konkretną godzinę dostawy?', a: 'Możesz wybrać dostępny przedział czasowy. Wskazana godzina jest orientacyjna - dokładny czas doręczenia może zależeć od liczby zamówień i sytuacji na drodze.' },
      { group: 'delivery', q: 'Czy dostawa w godzinach 19:00–21:00 jest dodatkowo płatna?', a: 'Tak, dostawa w godzinach wieczornych (19:00–21:00) wiąże się z dodatkową opłatą +20 zł.' },
      { group: 'delivery', q: 'Co jeśli odbiorcy nie będzie w domu?', a: 'Kurier najpierw kontaktuje się z odbiorcą. Jeśli nie odbiera, dzwonimy do osoby zamawiającej. Za zgodą odbiorcy możemy zostawić kwiaty u sąsiada, ustalić odbiór w kwiaciarni albo pozostawić je pod drzwiami, jeśli warunki na to pozwalają i odbiorca wkrótce wróci.' },
      { group: 'delivery', q: 'Czy kurier poinformuje odbiorcę, że dostarcza kwiaty?', a: 'Nie. Kurier nie zdradza, co jest w przesyłce. Dostawa ma pozostać niespodzianką.' },
      { group: 'delivery', q: 'Co jeśli odbiorca nie odbiera telefonu?', a: 'Jeśli nie uda się skontaktować ani z odbiorcą, ani z zamawiającym, kurier wraca z zamówieniem do kwiaciarni. W razie potrzeby ponownej dostawy - jest ona dodatkowo płatna.' },
      { group: 'delivery', q: 'Czy mogę odebrać zamówienie osobiście?', a: 'Tak. Możesz bezpłatnie odebrać zamówienie w dowolnej z naszych dwóch kwiaciarni w Lublinie.' },
      { group: 'delivery', q: 'W jakich godzinach można odebrać zamówienie?', a: 'Odbiór osobisty jest możliwy od poniedziałku do soboty w godzinach 9:00–19:00 oraz w niedzielę 10:00–16:00.' },
      { group: 'payment', q: 'Jak mogę zapłacić za zamówienie?', a: 'Obsługujemy kartę płatniczą (Visa, Mastercard), BLIK oraz przelew online przez Przelewy24. Wszystkie płatności obsługuje Stripe.' },
      { group: 'payment', q: 'Czy mogę zapłacić przy odbiorze?', a: 'Płatność gotówką przy odbiorze nie jest dostępna online. Wszystkie zamówienia przez bukietowa.pl wymagają płatności z góry.' },
      { group: 'payment', q: 'Co zrobić, jeśli płatność nie przeszła?', a: 'Sprawdź, czy środki nie zostały pobrane. Jeśli konto nie jest obciążone, możesz ponownie złożyć zamówienie. W razie wątpliwości skontaktuj się z nami.' },
      { group: 'payment', q: 'Pieniądze pobrane, a zamówienie bez potwierdzenia - co robić?', a: 'Skontaktuj się z nami jak najszybciej, podając kwotę i datę transakcji. Sprawdzimy status i wyjaśnimy sytuację.' },
      { group: 'payment', q: 'Czy otrzymam potwierdzenie płatności?', a: 'Tak. Po pomyślnej płatności automatycznie wyślemy potwierdzenie zamówienia na podany adres e-mail.' },
      { group: 'payment', q: 'Czy mogę otrzymać fakturę?', a: 'Tak, na życzenie wystawiamy faktury VAT. Skontaktuj się z nami po złożeniu zamówienia i podaj dane do faktury.' },
    ] satisfies FaqEntry[],
  },
  chapterDivider: {
    left: 'Dostawa',
    center: 'Płatności',
    right: 'Stripe · PCI DSS',
  },
  payment: {
    eyebrow: 'Płatność online',
    heading: 'Wygodne i bezpieczne płatności',
    methodsLabel: 'Akceptowane metody',
    securityLabel: 'Bezpieczeństwo',
    processLabel: 'Jak wygląda płatność?',
    methods: [
      { iconKey: 'card', title: 'Karta płatnicza', desc: 'Visa, Mastercard - debetowe i kredytowe' },
      { iconKey: 'blik', title: 'BLIK', desc: '6-cyfrowy kod z aplikacji bankowej' },
      { iconKey: 'p24', title: 'Przelewy24', desc: 'Szybki przelew online z konta bankowego' },
    ] satisfies PaymentMethodContent[],
    securityItems: [
      'Dane karty nie trafiają na nasz serwer',
      'Połączenie szyfrowane protokołem TLS',
      'Operator certyfikowany PCI DSS Level 1',
      'Weryfikacja 3D Secure tam, gdzie wymagana',
    ] as string[],
    processSteps: [
      { n: '1', title: 'Wybierz metodę', desc: 'Karta, BLIK lub przelew - w koszyku.' },
      { n: '2', title: 'Bezpieczny formularz', desc: 'Stripe szyfruje Twoje dane - my ich nie widzimy.' },
      { n: '3', title: 'Potwierdzenie e-mail', desc: 'Automatycznie po pomyślnej płatności.' },
    ] satisfies PaymentProcessStep[],
  },
};
